import React from 'react';
import {
  DELETE_ENTITY_TYPE,
  DELETION_TYPE,
} from '~/components/page/Settings/utils/constants';
import { navigate } from '@gatsbyjs/reach-router';
import useIsMounted from '~/hooks/useIsMounted';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import useSessionHydration from '~/hooks/useSessionHydration';
import { Highlight } from '~/components/atom/Typography';
import { useDeleteOfficeMutation, UserStatus } from '~/graphql/types';
import DeleteEntityModal from '../components/DeleteEntityModal';
import type { ListItemForDeletion } from '../components/ListItemCard';
import useUsers from '~/hooks/useUsers';
import useTrackEvent from '~/hooks/useTrackEvent';

const text = {
  modalHeader: (
    <>
      Vestiging verwijderen uit je <Highlight>account</Highlight>{' '}
    </>
  ),
  modalDescription:
    'Wijs contacten en openstaande taken toe aan een andere gebruiker of vestiging',
  userIsBeingDeleted: (
    <>
      Even geduld. De gebruiker wordt uit de vestiging verwijderd. <br />
      Dit kan enkele seconden duren. Ververs daarna de pagina en probeer het
      opnieuw.
    </>
  ),
};

type Props = {
  list: Array<ListItemForDeletion>;

  /** When the modal is closed */
  onClose: () => void;

  /** When the cancel button is pressed */
  onCancel: () => void;

  /** The id of the office that we want to delete */
  officeId: string;
};
const DeleteOfficeModal: React.FCC<Props> = ({
  list,
  onClose,
  onCancel,
  officeId,
}) => {
  const [, refetchSessionHydration] = useSessionHydration();
  const isMounted = useIsMounted();
  const trackEvent = useTrackEvent();
  const account = useCurrentAccount();
  const users = useUsers({ officeIds: [officeId] });

  const hasDeletingStatus = users.some(user =>
    user.officeRelations.find(
      relation => relation.userStatus === UserStatus.Deleting,
    ),
  );
  const [deleteOffice, { error }] = useDeleteOfficeMutation();
  return (
    <DeleteEntityModal
      message={{
        level: 'warning',
        message: text.userIsBeingDeleted,
        icon: { name: 'loading', backgroundColor: { group: 'tertiary' } },
      }}
      list={list}
      onClose={onClose}
      onCancel={onCancel}
      modalHeader={text.modalHeader}
      modalDescription={text.modalDescription}
      entityId={officeId}
      entityType={DELETE_ENTITY_TYPE.OFFICE}
      loading={hasDeletingStatus}
      onSuccess={() => {
        if (!isMounted()) return;

        onClose();
        void navigate('/-/settings/offices');
        void refetchSessionHydration();
      }}
      onDelete={(changeSet, transferOfficeResourcesTo) =>
        deleteOffice({
          variables: {
            accountId: account.id,
            officeId: officeId,
            changeSet,
            transferOfficeResourcesTo,
          },
        }).then(result => {
          if (result == null || result.data == null) {
            return null;
          }

          if (result.data.deleteOffice.success) {
            /** ----- TRACKING: office_deleted ----- */
            trackEvent({
              eventName: 'office_deleted',
              parameters: { office_id: officeId },
            });
          }

          return result.data.deleteOffice;
        })
      }
      mutationError={error}
      deletionType={DELETION_TYPE.DELETE_OFFICE}
    />
  );
};

export default DeleteOfficeModal;
